import {
  Box,
  ButtonGroup,
  ButtonVariants,
  FormControlSizes,
  FormGroup,
  getDisplayNameOrNameLabel,
  productTypeLabel,
  SearchSelect,
  Text,
  ToggleButton,
  usePortal,
  type Customer,
  type MarketAccount,
  type ProductTypeEnum,
} from '@talos/kyoko';
import { PriceGridSymbolFilters, usePriceGridFilterControls } from '../../filters';
import { PriceGridHeaderContainer, PriceGridHeaderFilters } from '../../PriceGridHeaderLayout';
import type { PriceGridFilterState, PriceGridHeaderParams } from '../../types';

interface CustomerPriceGridHeaderProps extends PriceGridHeaderParams {
  customers: Customer[];
  selectedCustomer: Customer | undefined;
  onCustomerChanged: (customer: Customer | undefined) => void;
  marketAccounts: MarketAccount[];
  selectedMarketAccount: MarketAccount | undefined;
  onMarketAccountChanged: (marketAccount: MarketAccount | undefined) => void;
  filterState: PriceGridFilterState;
  onFilterStateChanged: (filterState: PriceGridFilterState) => void;
  availableProductTypes: ProductTypeEnum[];
}

export function CustomerPriceGridHeader({
  filtersPortalId,
  customers,
  selectedCustomer,
  onCustomerChanged,
  marketAccounts,
  selectedMarketAccount,
  onMarketAccountChanged,
  filterState,
  onFilterStateChanged,
  availableProductTypes,
}: CustomerPriceGridHeaderProps) {
  const { setPortalRef: filtersPortalRef } = usePortal(filtersPortalId);
  const filterControls = usePriceGridFilterControls({ filterState, onFilterStateChanged });
  const selectedProductTypes = filterState.productTypes ?? [];

  const handleProductTypeToggled = (productType: ProductTypeEnum) => {
    const selectedProductTypesSet = new Set(selectedProductTypes);
    if (selectedProductTypesSet.has(productType)) {
      selectedProductTypesSet.delete(productType);
    } else {
      selectedProductTypesSet.add(productType);
    }

    const nextProductTypes = availableProductTypes.filter(value => selectedProductTypesSet.has(value));
    onFilterStateChanged({
      ...filterState,
      productTypes: nextProductTypes.length === 0 ? undefined : nextProductTypes,
    });
  };

  return (
    <PriceGridHeaderContainer data-testid="price-grid-header">
      <PriceGridHeaderFilters>
        <Text color="colorTextImportant" fontWeight={500}>
          Customer Price Grid
        </Text>
        <FormGroup
          inline
          alignItems="center"
          label="Customer"
          labelWidth="6rem"
          mb="0"
          controlId="CustomerPriceGridCustomer"
        >
          <SearchSelect
            dropdownWidth="250px"
            dropdownPlacement="bottom-start"
            selection={selectedCustomer}
            options={customers}
            portalize={true}
            size={FormControlSizes.Small}
            dropdownSize={FormControlSizes.Small}
            getLabel={getDisplayNameOrNameLabel}
            onChange={onCustomerChanged}
            data-testid="customer-price-grid-customer-selector"
          />
        </FormGroup>
        <FormGroup
          inline
          alignItems="center"
          label="Market Account"
          labelWidth="7rem"
          mb="0"
          controlId="CustomerPriceGridMarketAccount"
        >
          <SearchSelect
            dropdownWidth="250px"
            dropdownPlacement="bottom-start"
            selection={selectedMarketAccount}
            options={marketAccounts}
            disabled={!selectedCustomer || marketAccounts.length <= 1}
            portalize={true}
            size={FormControlSizes.Small}
            dropdownSize={FormControlSizes.Small}
            getLabel={getDisplayNameOrNameLabel}
            onChange={onMarketAccountChanged}
            data-testid="customer-price-grid-market-account-selector"
          />
        </FormGroup>
        {availableProductTypes.length > 0 && (
          <Box display="flex" alignItems="center" alignSelf="center">
            <ButtonGroup size={FormControlSizes.Small}>
              {availableProductTypes.map(productType => {
                const selected = selectedProductTypes.includes(productType);
                return (
                  <ToggleButton
                    key={productType}
                    grouped
                    selected={selected}
                    selectedVariant={ButtonVariants.Priority}
                    unselectedVariant={ButtonVariants.Default}
                    size={FormControlSizes.Small}
                    onClick={() => handleProductTypeToggled(productType)}
                  >
                    {productTypeLabel(productType)}
                  </ToggleButton>
                );
              })}
            </ButtonGroup>
          </Box>
        )}
        <PriceGridSymbolFilters controls={filterControls} />
        <Box ref={filtersPortalRef} />
      </PriceGridHeaderFilters>
    </PriceGridHeaderContainer>
  );
}
